'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle } from 'lucide-react';
import type { Drone } from '@/types/fleet';
import { MISSION_TYPE_LABELS, type MissionType } from '@/lib/mission-type';
import { utcInputValueToIso, type MissionListStatus } from './missionDetailShared';
import { cn } from '@/lib/utils';
import { aMissionType } from '@/lib/copy';
import type { FormState } from './coverageTypes';
import { UNAVAILABLE_DRONE_STATUSES } from './coveragePresentation';
import { describeApiError, formatCoord, formatDuration, localEcho } from './coverageFormatters';
import { Field } from './CoveragePrimitives';
import { CoverageAircraftStep } from './CoverageAircraftStep';
import { CoverageRulesStep } from './CoverageRulesStep';
import { CoverageTimelinePreview } from './CoverageTimelinePreview';
import { CoverageProjectionVerdict } from './CoverageProjectionVerdict';
import { useCoveragePlanInputs, projectionDock } from '@/hooks/useCoveragePlanInputs';
import { projectCoverage, type AircraftPlan, type ProjectionAircraft } from '@/lib/coverageProjection';

/**
 * ATTACHING CONTINUOUS COVERAGE, as three steps over one form (PR 3).
 *
 * WHAT WAS WRONG WITH THE FORM THIS REPLACES. Five numbers in a row that mean nothing alike - a
 * service-level target styled identically to two battery thresholds - a window whose commonest
 * case was a 10 px footnote describing an empty field, aircraft picked in click order with none of
 * the three facts that decide whether the set works, and no prediction of any kind. Attaching an
 * unattended multi-hour rotation was a commit with no preview, and the only way to find out how
 * many aircraft a window needed was to attach, wait, and read the achieved percentage afterwards.
 *
 * THREE STEPS, NOT A GATE. They are tabs over ONE form, all three reachable at any time and every
 * field pre-filled with a conservative default - an operator who agrees with the defaults still
 * presses once. A linear wizard would make the common case slower than the form it replaces, which
 * is how a redesign loses to the thing it fixed.
 *
 * EVERY PRE-PRESS REFUSAL SURVIVES, and that is this component's first acceptance criterion. The
 * old form named every reason an attach could not be made BEFORE the press, because sending them
 * to the API produces a toast that names none of them. Splitting the fields across three panels is
 * exactly where such a sentence gets lost, so each step header carries the count of refusals
 * belonging to it and the button still names the first one.
 */

/**
 * Conservative starting values, not empty boxes: 95% of time, land at 20%, shout at 5% - so an
 * operator who agrees with them presses once. `critical_action` is deliberately NOT here: stage 1
 * only ever means `alert_only`, and a control offering AUTO RTH would promise something no code
 * path performs. The form says so in words instead.
 */
export const INITIAL_FORM: FormState = {
  radiusM: '250',
  stationAltitudeM: '',
  coverageTargetPct: '95',
  batteryNormalPct: '20',
  batteryCriticalPct: '5',
  windowStart: '',
  windowEnd: '',
  memberIds: [],
  // 044, and the one default here chosen for continuity rather than for being the best answer.
  // `full_route` is what every relief flies today, so an operator who does not open the Rules tab
  // gets the behaviour they already have rather than a shorter approach nobody asked for.
  reliefApproach: 'full_route',
};

/**
 * The mission's aircraft, but only if the fleet list actually holds it.
 *
 * `assignedDroneId` is an id from the mission row; `drones` is what this screen can render and the
 * operator can remove. When they disagree - a deleted airframe, one in another org, or simply a
 * roster read that has not answered yet - seeding blind puts a member in `memberIds` that no row
 * draws, no Remove button reaches, and no projection scores, while DEFEATING the empty-rotation
 * refusal that is the only thing standing between that state and a POST. See `seedBlockers`.
 */
function resolveSeed(assignedDroneId: string | null | undefined, drones: Drone[]): string | null {
  if (!assignedDroneId) return null;
  return drones.some((d) => d.id === assignedDroneId) ? assignedDroneId : null;
}

/**
 * The starting form for THIS mission, with the aircraft already flying it in the rotation.
 *
 * `memberIds` used to start empty, and that was the single worst moment in the flow: an operator
 * who had just chosen an aircraft in compose was shown a rotation picker with nothing in it and no
 * mention of the choice they had made one panel earlier. Worse than the repeated click, the two
 * picks are genuinely independent - `missions.assigned_drone_id` and `mission_rotation_members` are
 * different tables, and `coverageRepo.findTrackedAircraft` UNIONs the assigned aircraft into the
 * rotation at `rotation_order = 0` with its own comment noting "nothing at attach time forces that".
 * So picking -01 in compose and -02/-03 here built a THREE-aircraft rotation led by -01, and no
 * surface said so.
 *
 * Seeding it makes the two sets agree by default, which turns that UNION back into the safety net
 * it was written to be rather than the normal path.
 *
 * IT IS A DEFAULT AND NOT A LOCK. The seeded aircraft can be removed like any other - a rotation
 * led by an airframe other than the one flying the mission is a configuration the server supports
 * on purpose, and removing it to fix a presentation problem would cost more than it fixed.
 */
export function initialForm(seedDroneId?: string | null): FormState {
  // The caller resolves against the fleet first (`resolveSeed`). This takes an id it can trust.
  // A COPY in both branches: returning the module constant by reference is safe only for as long
  // as every write goes through `setForm(prev => ({ ...prev }))`, and one in-place mutation of
  // `form.memberIds` would then corrupt the default for every future mount in the session.
  return { ...INITIAL_FORM, memberIds: seedDroneId ? [seedDroneId] : [] };
}

export type WizardStep = 'area' | 'aircraft' | 'rules';

const STEP_ORDER: readonly WizardStep[] = ['area', 'aircraft', 'rules'];

const STEP_LABEL: Record<WizardStep, string> = {
  area: 'Area & window',
  aircraft: 'Aircraft',
  rules: 'Rules',
};

/**
 * Which step a refusal belongs to, so a step that holds one can say so on its own tab.
 *
 * A refusal an operator cannot see is the same as no refusal at all: the whole point of naming
 * them before the press is that the fix is in view. Three panels means two of them are always out
 * of view, and this is what puts the marker back on the tab.
 */
export interface StepBlocker {
  readonly step: WizardStep;
  readonly message: string;
}

/** The window presets. UNTIL STOPPED is first because it is what most watches actually are. */
const WINDOW_PRESETS: readonly { id: string; label: string; hours: number | null }[] = [
  { id: 'open', label: 'Until stopped', hours: null },
  { id: 'h4', label: 'Next 4 h', hours: 4 },
  { id: 'h8', label: 'Next 8 h', hours: 8 },
  { id: 'h12', label: 'Next 12 h', hours: 12 },
];

/** A `datetime-local` value in UTC, which is what the two boxes and `utcInputValueToIso` speak. */
function toUtcInputValue(d: Date): string {
  return d.toISOString().slice(0, 16);
}

/**
 * A half-typed box gives `null` from `utcInputValueToIso`, and the projection needs to tell that
 * apart from "no bound was set". An unreadable Date carries the difference; `null` here would
 * silently mean UNTIL STOPPED and answer a question the operator has not finished asking.
 */
function isoToDate(iso: string | null): Date {
  return iso === null ? new Date(Number.NaN) : new Date(iso);
}

/**
 * How often the preview's clock advances.
 *
 * Nothing on a projection moves by itself: the plan starts at the window's own start, and for an
 * UNTIL STOPPED watch that is `now`. A wizard left open while the operator is called away would
 * otherwise draw a plan beginning twenty minutes ago, with the NOW rule frozen there - the same
 * defect the live chart's staleness ceiling exists to prevent, reached from the other side.
 */
const PREVIEW_CLOCK_MS = 60_000;

/** How long the attach may take before it is given up on. See the call site for why it exists. */
const ATTACH_TIMEOUT_MS = 15_000;

/**
 * "Coverage does not attach to this kind of mission", or null when it does.
 *
 * Exported because the panel says the same thing in a place this component never renders: when
 * the type cannot carry coverage there is NO FORM AT ALL, since six inputs and an armed button
 * over a mission the server will always refuse is a form whose only outcome is an error toast. Two
 * copies of the sentence would drift the day a second mission type is added.
 */
export function capabilityBlocker(
  capableTypes: MissionType[] | null,
  missionType: MissionType,
): string | null {
  if (capableTypes === null || capableTypes.includes(missionType)) return null;
  const supported = capableTypes.map((t) => MISSION_TYPE_LABELS[t]).join(', ');
  return capableTypes.length === 0
    ? 'This server reports no mission type that can carry coverage'
    : `Coverage attaches to ${supported}, and this is ${aMissionType(missionType)}`;
}

export interface CoverageAttachWizardProps {
  missionId: string;
  missionType: MissionType;
  /** The mission's last waypoint - the station coverage is measured at. Null when it has no route. */
  hold: { latitude: number; longitude: number; altitude: number } | null;
  /**
   * The aircraft assigned to the mission, seeded as the first rotation member. See `initialForm`.
   *
   * Optional and nullable, because both are real: a mission can be created with no aircraft on the
   * plan lane, and a caller that has not been given the value yet must render a working form rather
   * than throw.
   */
  assignedDroneId?: string | null;
  drones: Drone[];
  dronesStatus: MissionListStatus;
  onRetryDrones: () => void;
  /** The server's allow-list, or null while it is unknown. */
  capableTypes: MissionType[] | null;
  /** Something else on the panel is mid-flight; every control here goes inert. */
  busy: boolean;
  submitting: boolean;
  onSubmittingChange: (v: boolean) => void;
  /** Coverage was attached. The panel re-reads itself and tells the page the mission moved. */
  onAttached: () => void | Promise<void>;
}

export function CoverageAttachWizard({
  missionId, missionType, hold, assignedDroneId = null, drones, dronesStatus, onRetryDrones,
  capableTypes, busy, submitting, onSubmittingChange, onAttached,
}: CoverageAttachWizardProps) {
  const uid = useId();
  const seedDroneId = resolveSeed(assignedDroneId, drones);
  const [form, setForm] = useState<FormState>(() => initialForm(seedDroneId));
  /**
   * The operator has touched the rotation, so it is theirs and the seed stops writing to it.
   *
   * Without this the effect below would be a loop: it re-seeds, the operator removes the seeded
   * aircraft, it re-seeds again, and the row cannot be got rid of - which is precisely the "default,
   * not a lock" promise inverted.
   */
  const rotationTouched = useRef(false);

  /**
   * RE-SEED WHEN THE ANSWER CHANGES, while the rotation is still untouched.
   *
   * The mount-time seed is a lazy initialiser, so it reads `drones` exactly once - and at that
   * moment the roster is very often still in flight. On the COP the sheet re-reads it on open by
   * design, so the FIRST render is reliably the empty list: without this, seeding would work only
   * by luck of timing. It also covers the mission page, where this form stays mounted for as long
   * as a mission has no coverage and `assigned_drone_id` is a live prop the operator can change in
   * the sidebar beside it.
   */
  useEffect(() => {
    if (rotationTouched.current) return;
    setForm((prev) => {
      const next = seedDroneId ? [seedDroneId] : [];
      if (prev.memberIds.length === next.length
        && prev.memberIds.every((id, i) => id === next[i])) return prev;
      return { ...prev, memberIds: next };
    });
  }, [seedDroneId]);
  const [step, setStep] = useState<WizardStep>('area');
  /** Which preset the operator chose. `custom` is the two boxes, one click in. */
  const [windowPreset, setWindowPreset] = useState<string>('open');
  /** The preview's clock. See `PREVIEW_CLOCK_MS`. */
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), PREVIEW_CLOCK_MS);
    return () => clearInterval(id);
  }, []);

  const toggleMember = (droneId: string) => {
    rotationTouched.current = true;
    setForm((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(droneId)
        ? prev.memberIds.filter((id) => id !== droneId)
        : [...prev.memberIds, droneId],
    }));
  };

  /**
   * ROTATION ORDER IS THE OPERATOR'S, and this is how they set it.
   *
   * It was click order with no way to change it, which made the order an accident of the sequence
   * somebody happened to tap in. The order decides which aircraft is on station first and which
   * dock is being counted on when - `member_drone_ids` is ordered on the wire and the server reads
   * position as `rotation_order`.
   */
  const moveMember = (droneId: string, direction: -1 | 1) => {
    rotationTouched.current = true;
    setForm((prev) => {
      const at = prev.memberIds.indexOf(droneId);
      const to = at + direction;
      if (at < 0 || to < 0 || to >= prev.memberIds.length) return prev;
      const next = [...prev.memberIds];
      [next[at], next[to]] = [next[to], next[at]];
      return { ...prev, memberIds: next };
    });
  };

  /**
   * The three facts step 2 shows about each aircraft, and the inputs S7's preview draws from.
   *
   * Fetched only while the wizard is on screen - the panel is collapsed by default and this is two
   * reads, so they fire when an operator opens the thing that asks the question.
   */
  const planInputs = useCoveragePlanInputs(missionId, true);

  /**
   * Each picked aircraft reduced to what the projection can say about it.
   *
   * `planAircraft` is the same pure function the preview uses, so the sentence on a row and the
   * bars on the chart cannot disagree about whether an aircraft can hold the station.
   */
  const battery = Number(form.batteryNormalPct);
  const batteryUsable = Number.isFinite(battery) && battery > 0 && battery <= 100;
  /**
   * Whether a plan can be computed at all, and why not when it cannot.
   *
   * Both causes are WHOLE-FORM problems living on a different step from the rows they blank, so
   * without this step 2 rendered a rotation of dashes that looked exactly like a fleet this
   * platform cannot size.
   */
  const notComputableReason = hold === null
    ? 'This mission has no route yet, so there is no station to measure a transit to.'
    : !batteryUsable
      // Points at the sentence, not at a label: step 3's battery figures are embedded in prose now
      // and there is no box called "Land at %" to go and find.
      ? 'Set the battery policy on the Rules step before these figures can be worked out.'
      : null;

  /** The rotation as the projection needs it: ordered, with the facts the browser cannot compute. */
  const projectionMembers = useMemo<ProjectionAircraft[]>(() => form.memberIds.flatMap((id) => {
    const drone = drones.find((d) => d.id === id);
    if (!drone) return [];
    const energy = planInputs.energyByDrone.get(id);
    return [{
      droneId: id,
      callsign: drone.callsign,
      name: drone.name,
      enduranceMinutesAtFull: energy?.enduranceMinutesAtFull ?? null,
      cruiseSpeedMs: energy?.cruiseSpeedMs ?? null,
      dock: drone.dock_id === null
        ? null
        : projectionDock(planInputs.dockById.get(drone.dock_id)),
    }];
  }), [form.memberIds, drones, planInputs.energyByDrone, planInputs.dockById]);

  /**
   * THE PLAN THIS CONFIGURATION WOULD FLY, recomputed on every change to the form.
   *
   * `now` is held in state and ticked slowly rather than read here, for two reasons: a pure
   * function must not read a clock, and a projection that re-derived `Date.now()` on every render
   * would move under the operator while they type. The tick is what stops the other failure - a
   * wizard left open for twenty minutes drawing a plan that starts twenty minutes ago.
   */
  const projection = useMemo(() => projectCoverage({
    now,
    missionId,
    station: hold === null
      // Refused inside the projection rather than guarded here, so there is ONE place that decides
      // what an unusable input produces.
      ? { lat: Number.NaN, lon: Number.NaN }
      : { lat: hold.latitude, lon: hold.longitude },
    windowStart: form.windowStart === '' ? null : isoToDate(utcInputValueToIso(form.windowStart)),
    windowEnd: form.windowEnd === '' ? null : isoToDate(utcInputValueToIso(form.windowEnd)),
    batteryNormalPct: battery,
    members: projectionMembers,
    // The org's configured swap, so the preview sizes a swap dock's turnaround with the number the
    // planner will actually use after the attach. It rides `planInputs.status` with the endurance
    // and dock reads, so a failed read suppresses the preview rather than substituting a default
    // into the figure the MEETS/UNDER verdict is computed from.
    swapDurationSeconds: planInputs.swapDurationSeconds,
  }), [
    now, missionId, hold, form.windowStart, form.windowEnd, battery, projectionMembers,
    planInputs.swapDurationSeconds,
  ]);

  /**
   * The per-aircraft plans READ OFF THE PROJECTION, not computed a second time.
   *
   * Two calls to `planAircraft` with the same inputs would agree today and drift the first time
   * one caller's arguments changed - and the two surfaces are a row saying an aircraft cannot hold
   * the station and a chart drawing it holding one.
   */
  const plansByDrone = useMemo(() => {
    const out = new Map<string, AircraftPlan>();
    for (const plan of projection.verdict.plans) out.set(plan.droneId, plan);
    return out;
  }, [projection]);

  const enduranceSourceByDrone = useMemo(() => {
    const out = new Map<string, string | null>();
    for (const [id, e] of planInputs.energyByDrone) out.set(id, e.enduranceSource);
    return out;
  }, [planInputs.energyByDrone]);

  const cruiseSourceByDrone = useMemo(() => {
    const out = new Map<string, string | null>();
    for (const [id, e] of planInputs.energyByDrone) out.set(id, e.cruiseSource);
    return out;
  }, [planInputs.energyByDrone]);

  /**
   * How many picked aircraft the platform cannot size, for the advisory beside the button.
   *
   * ADVISORY, NOT A BLOCKER: the server accepts these and the rotation will fly. But the cost is
   * not "a missing bar" - `computeReplacementTiming` refuses on the same missing inputs with
   * `launch_now`, so such an aircraft is relieved the moment it reaches the station. That fact
   * lives on the row, on a step that is `hidden` from the other two, and the press is available
   * from all three - so the count is hoisted here exactly as `unavailablePicked` is.
   */
  const unsizablePicked = form.memberIds.filter((id) => plansByDrone.get(id)?.refusal != null).length;

  /**
   * The operator's pass mark, or null while there is not one.
   *
   * `Number('')` is 0 and finite, so backspacing the box to retype it produced a GREEN "MEETS the
   * 0% pass mark" - a pass badge against a threshold nobody set, at the exact moment the operator
   * is editing the threshold. The panel's rule throughout is that an absent value is never
   * rendered as a zero. Bounded the same way the blocker beside it is.
   */
  const passMarkPct = useMemo(() => {
    if (form.coverageTargetPct.trim() === '') return null;
    const n = Number(form.coverageTargetPct);
    return Number.isFinite(n) && n > 0 && n <= 100 ? n : null;
  }, [form.coverageTargetPct]);

  /**
   * The one sentence a screen reader gets instead of the chart.
   *
   * The verdict rather than a description of the picture, because before an attach the answer IS
   * the arithmetic - "94% of the window, longest gap 6m" is what the operator came for, and
   * "three lanes with bars on them" is not.
   */
  const previewSummary = useMemo(() => {
    const v = projection.verdict;
    if (!v.scorable) return 'This rotation cannot be scored yet.';
    const covered = (v.coveredPct ?? 0).toFixed(1);
    const gap = v.longestGapSeconds === null || v.longestGapSeconds === 0
      ? 'no gap'
      : `longest gap ${formatDuration(v.longestGapSeconds)}`;
    return `Estimated ${covered}% of the window covered, ${gap}.`;
  }, [projection]);

  /**
   * A preset writes the two boxes rather than replacing them.
   *
   * One representation of the window, so the custom boxes always show what a preset chose and
   * `handleAttach` has exactly one thing to read. A preset that lived beside the boxes as a
   * separate piece of state is two answers to "when does this watch run".
   */
  const applyPreset = (id: string) => {
    setWindowPreset(id);
    const preset = WINDOW_PRESETS.find((p) => p.id === id);
    if (!preset) return; // 'custom' - leave whatever is typed alone
    if (preset.hours === null) {
      setForm((prev) => ({ ...prev, windowStart: '', windowEnd: '' }));
      return;
    }
    const now = new Date();
    setForm((prev) => ({
      ...prev,
      windowStart: toUtcInputValue(now),
      windowEnd: toUtcInputValue(new Date(now.getTime() + preset.hours! * 3600_000)),
    }));
  };

  /**
   * Every reason this attach cannot be made, in the operator's words, BEFORE the press - each one
   * tagged with the step whose field fixes it. Every entry is knowable here; sending them to be
   * refused produces a toast that names none of them.
   */
  const blockers = useMemo<StepBlocker[]>(() => {
    const out: StepBlocker[] = [];
    const capability = capabilityBlocker(capableTypes, missionType);
    if (capability !== null) out.push({ step: 'area', message: capability });
    if (!hold) {
      out.push({
        step: 'area',
        message: 'This mission has no route yet - coverage needs a hold point to watch from',
      });
    }
    // ONE AIRCRAFT IS A ROTATION (#564), and this blocker is why the feature was unreachable from
    // the screen for the whole deployment shape it was built for: one dock, one airframe.
    //
    // WHY THE FLEET'S OWN STATE IS IN THE SENTENCE. The picker is behind a closed tab now, and
    // "pick at least one aircraft" over a fleet that failed to load is an instruction the operator
    // cannot carry out - the list they are being sent to does not exist, and the Retry that would
    // fix it is inside the panel they have not opened. Three states, three sentences, the same
    // discipline `dronesStatus` was introduced for: an org with 40 aircraft told "no aircraft in
    // the fleet" because a request 500'd would go and add a 41st.
    if (form.memberIds.length < 1) {
      out.push({
        step: 'aircraft',
        message: dronesStatus === 'error'
          ? 'The fleet could not be loaded, so there is nothing to pick a rotation from'
          : dronesStatus === 'loading'
            ? 'Waiting for the fleet before a rotation can be picked'
            : drones.length === 0
              ? 'There are no aircraft in this fleet to build a rotation from'
              : 'Pick at least one aircraft for the rotation',
      });
    }
    /**
     * A MEMBER THE OPERATOR CANNOT SEE IS A REFUSAL, not a row that happens not to render.
     *
     * `memberIds` can hold an id the fleet list does not: the seed resolves against `drones` and
     * re-derives when they arrive, but a roster that FAILS after a successful one leaves a member
     * picked from a list that is now empty. `CoverageAircraftStep` draws rows by `drones.find`, so
     * such a member is invisible; `projectionMembers` flatMaps it away, so the preview scores a
     * rotation that is not the one being committed; and `commitSummary` falls back to
     * "AIRCRAFT 1". Every surface goes quiet and the press stays enabled.
     *
     * This is the fail-closed half of the seed. Without it, seeding an unresolvable id would have
     * REMOVED the empty-rotation refusal below - turning a state that used to disable Attach into
     * one that POSTs an unattended multi-hour rotation whose only aircraft was never on screen.
     */
    const unresolved = form.memberIds.filter((id) => !drones.some((d) => d.id === id));
    if (unresolved.length > 0) {
      out.push({
        step: 'aircraft',
        message: dronesStatus === 'error'
          ? `The fleet could not be loaded, so ${unresolved.length === 1 ? 'an aircraft' : `${unresolved.length} aircraft`} in this rotation cannot be shown or checked - retry the fleet before attaching`
          : `${unresolved.length === 1 ? 'An aircraft' : `${unresolved.length} aircraft`} in this rotation is not in the fleet - remove it, or retry the fleet`,
      });
    }
    // The server's other refusal, named BEFORE the press. A rotation of one has no relief, so its
    // dock is the whole of its resilience: without one it holds the station until its reserve is
    // gone and then has nowhere to go.
    if (form.memberIds.length === 1) {
      const solo = drones.find((d) => d.id === form.memberIds[0]);
      if (solo && solo.dock_id === null) {
        out.push({
          step: 'aircraft',
          message: `${solo.callsign ?? solo.name} is stationed in no dock - a rotation of one has to`
            + ' return somewhere to be resupplied',
        });
      }
    }
    // Both ENDS of every bound, matching the API's Zod schema. Checking only `> 0` left four
    // refusals - a radius over 50 km, a percentage over 100, a station altitude over 500 m - to
    // come back from the server as a bare "Validation failed". `min`/`max` on the input do not
    // constrain a typed value.
    for (const [label, value, max, stepOf] of [
      ['Radius', form.radiusM, 50000, 'area'],
      ['Coverage %', form.coverageTargetPct, 100, 'rules'],
      ['Land at %', form.batteryNormalPct, 100, 'rules'],
      ['Critical %', form.batteryCriticalPct, 100, 'rules'],
    ] as const) {
      // `Number('')` is 0, which the API refuses as `.gt(0)` - with a bare "Validation failed".
      if (value.trim() === '' || !Number.isFinite(Number(value)) || Number(value) <= 0) {
        out.push({ step: stepOf, message: `${label} needs a number above zero` });
      } else if (Number(value) > max) {
        out.push({ step: stepOf, message: `${label} cannot be above ${max}` });
      }
    }
    if (form.stationAltitudeM.trim() !== '') {
      const alt = Number(form.stationAltitudeM);
      if (!Number.isFinite(alt) || alt <= 0 || alt > 500) {
        out.push({ step: 'area', message: 'Station altitude must be between 1 and 500 m' });
      }
    }
    if (Number(form.batteryCriticalPct) >= Number(form.batteryNormalPct)) {
      out.push({ step: 'rules', message: 'Critical % must be below the normal landing target' });
    }
    const startSet = form.windowStart !== '';
    const endSet = form.windowEnd !== '';
    if (startSet !== endSet) {
      out.push({
        step: 'area',
        message: 'A window needs both a start and an end - leave both empty for UNTIL STOPPED',
      });
    } else if (startSet && endSet) {
      const from = utcInputValueToIso(form.windowStart);
      const to = utcInputValueToIso(form.windowEnd);
      if (from === null || to === null) {
        out.push({ step: 'area', message: 'The window dates could not be read' });
      } else if (Date.parse(to) <= Date.parse(from)) {
        out.push({ step: 'area', message: 'The window ends before it starts' });
      } else if (Date.parse(to) <= now.getTime()) {
        // THE TICKING CLOCK, not `Date.now()`. This memo has no clock of its own, so a bare read
        // froze the refusal at first render while the projection beside it kept advancing: a
        // window that expired while the operator was still deciding turned the verdict unscorable
        // and left Attach ENABLED, sending a refusal the server answers as a toast. Two surfaces
        // 40 px apart disagreeing about whether a configuration can be attached.
        out.push({ step: 'area', message: 'That coverage window has already ended' });
      }
    }
    return out;
  }, [capableTypes, missionType, hold, form, drones, dronesStatus, now]);

  /**
   * WHAT IS ABOUT TO BE COMMITTED, on screen at the moment of the press.
   *
   * Attach is deliberately live from any tab - that is what lets an operator who agrees with the
   * defaults press once. The cost is that the roster and the window are both behind panels that
   * are `hidden`, so it was possible to stand on the Rules tab and commit an unattended multi-hour
   * rotation with nothing on screen saying which aircraft were in it or when it ends. The flat form
   * this replaces always had both above the button.
   *
   * `operational-standards.md` asks the operator to understand the situation in under three
   * seconds; the one press where that matters most is this one.
   */
  const commitSummary = useMemo(() => {
    const picked = form.memberIds
      .map((id) => drones.find((d) => d.id === id))
      .map((d, i) => d?.callsign ?? d?.name ?? `AIRCRAFT ${i + 1}`);
    const roster = picked.length === 0
      ? 'NO AIRCRAFT'
      : `${picked.length} AIRCRAFT · ${picked.join(', ')}`;
    const window = form.windowStart === '' && form.windowEnd === ''
      ? 'UNTIL STOPPED'
      : `${form.windowStart || '—'}Z → ${form.windowEnd || '—'}Z`;
    const circle = `r${form.radiusM || '—'} m at ${form.stationAltitudeM || (hold ? String(hold.altitude) : '—')} m`;
    return `${roster} · ${window} · ${circle}`;
  }, [form, drones, hold]);

  /** How many picked aircraft cannot currently fly. Advisory; see UNAVAILABLE_DRONE_STATUSES. */
  const unavailablePicked = form.memberIds.filter(
    (id) => UNAVAILABLE_DRONE_STATUSES.has(drones.find((d) => d.id === id)?.status ?? ''),
  ).length;

  const blockersFor = (s: WizardStep) => blockers.filter((b) => b.step === s);

  const handleAttach = async () => {
    if (!hold || blockers.length > 0) return;
    onSubmittingChange(true);
    try {
      await api.post('/api/coverage', {
        mission_id: missionId,
        // The target defaults to the hold point itself - what the aircraft is watching from is
        // where it is watching. Editing that separately is a later stage's job.
        target: { kind: 'point', lat: hold.latitude, lon: hold.longitude, radius_m: Number(form.radiusM) },
        station_altitude_m: form.stationAltitudeM.trim() === '' ? undefined : Number(form.stationAltitudeM),
        // The house helper, not `new Date(value)`. A `datetime-local` value carries no offset and
        // ECMAScript parses it as LOCAL - so a box labelled UTC would silently shift by the
        // operator's offset, and an overnight watch typed as 22:00 in UTC+3 would be stored, and
        // read back, as 19:00Z.
        window_start: form.windowStart === '' ? null : utcInputValueToIso(form.windowStart),
        window_end: form.windowEnd === '' ? null : utcInputValueToIso(form.windowEnd),
        coverage_target_pct: Number(form.coverageTargetPct),
        handover_policy: 'propose',
        battery_normal_pct: Number(form.batteryNormalPct),
        battery_critical_pct: Number(form.batteryCriticalPct),
        critical_action: 'alert_only',
        // OFFERED, unlike the two literals above it, because all three values are honoured by the
        // launch the moment this is stored - see the Rules step's header for the rule this obeys.
        relief_approach: form.reliefApproach,
        member_drone_ids: form.memberIds,
      }, {
        /**
         * BOUNDED, because `submitting` is what holds the whole surface.
         *
         * `api.post`'s own comment names this hazard: "a command button awaiting this promise stays
         * wedged (and looks armed) for exactly as long as the request does." Inline on the mission
         * page that is a stuck button; the COP mounts this wizard in a MODAL whose dismissal is
         * refused while submitting, so a black-holed request there covered the live air picture -
         * with aircraft flying behind it - until the browser's own network timeout. One timeout
         * fixes both surfaces, which is why it lives here rather than at either call site.
         */
        timeoutMs: ATTACH_TIMEOUT_MS,
      });
      toast.success('Continuous coverage attached');
      // Back to the seeded default, not the empty one. This wizard stays mounted after a
      // successful attach on the mission page, and resetting to `INITIAL_FORM` would drop the
      // mission's own aircraft out of a form that is about to be shown again.
      rotationTouched.current = false;
      setForm(initialForm(seedDroneId));
      setWindowPreset('open');
      setStep('area');
      await onAttached();
    } catch (err) {
      toast.error(describeApiError(err, 'Failed to attach coverage'));
    } finally {
      onSubmittingChange(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* TABS, NOT A SEQUENCE. `role="tablist"` rather than a stepper, because every panel is
          reachable at any time and none of them gates another - which is also what lets an
          operator who agrees with the defaults go straight to Attach. */}
      <div role="tablist" aria-label="Coverage settings" className="flex flex-wrap gap-1">
        {STEP_ORDER.map((s, i) => {
          const count = blockersFor(s).length;
          return (
            <button
              key={s}
              type="button"
              role="tab"
              id={`${uid}-tab-${s}`}
              aria-selected={step === s}
              aria-controls={`${uid}-panel-${s}`}
              onClick={() => setStep(s)}
              className={cn(
                'min-h-7 px-2.5 py-1 rounded border text-[11px] uppercase tracking-wider transition-colors',
                'outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                step === s
                  ? 'border-primary/50 bg-primary/15 text-foreground'
                  : 'border-border bg-background text-muted-foreground hover:text-foreground',
              )}
            >
              <span className="text-primary">{i + 1}. </span>
              {STEP_LABEL[s]}
              {/* THE REFUSAL MARKER. Without it, splitting the fields across three panels hides two
                  thirds of the reasons an attach is refused behind a tab nobody has opened. */}
              {count > 0 && (
                <span className="ml-1 text-status-amber-fg" aria-label={`${count} to fix`}>
                  ({count})
                </span>
              )}
            </button>
          );
        })}
      </div>

      <fieldset disabled={busy} className="space-y-3 disabled:opacity-60">
        <legend className="sr-only">Continuous coverage settings</legend>

        <div
          role="tabpanel"
          id={`${uid}-panel-area`}
          aria-labelledby={`${uid}-tab-area`}
          hidden={step !== 'area'}
          className="space-y-3"
        >
          {/* ONE SENTENCE, NOT TWO LABELLED BOXES. Both numbers describe the same circle, and read
              as a sentence there is no guess about which unit belongs to which - which is exactly
              what "Radius (m)" beside "Station alt (m)" beside three percentages produced. */}
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>Hold a</span>
            <Input
              id={`${uid}-radius`}
              aria-label="Radius in metres"
              type="number"
              min={1}
              value={form.radiusM}
              onChange={(e) => setForm({ ...form, radiusM: e.target.value })}
              className="h-7 w-20 text-xs font-mono tabular-nums"
            />
            <span>m circle at</span>
            <Input
              id={`${uid}-alt`}
              aria-label="Station altitude in metres"
              type="number"
              min={1}
              placeholder={hold ? String(hold.altitude) : '—'}
              value={form.stationAltitudeM}
              onChange={(e) => setForm({ ...form, stationAltitudeM: e.target.value })}
              className="h-7 w-20 text-xs font-mono tabular-nums"
            />
            <span>
              m over the mission&apos;s last waypoint
              {hold ? ` - ${formatCoord(hold.latitude)}, ${formatCoord(hold.longitude)}` : ' - none placed yet'}.
            </span>
          </p>

          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
              Window
            </div>
            <div className="flex flex-wrap gap-2">
              {WINDOW_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={windowPreset === p.id}
                  onClick={() => applyPreset(p.id)}
                  className={cn(
                    'min-h-6 px-2 py-1 rounded border text-[11px] uppercase tracking-wider transition-colors',
                    'outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    windowPreset === p.id
                      ? 'border-primary/50 bg-primary/15 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:text-foreground',
                  )}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                aria-pressed={windowPreset === 'custom'}
                onClick={() => setWindowPreset('custom')}
                className={cn(
                  'min-h-6 px-2 py-1 rounded border text-[11px] uppercase tracking-wider transition-colors',
                  'outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                  windowPreset === 'custom'
                    ? 'border-primary/50 bg-primary/15 text-foreground'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                Custom
              </button>
            </div>

            {/* UNTIL STOPPED IS A CHOICE, NOT AN EMPTY FIELD. It was the commonest case and a 10 px
                footnote describing two blank boxes. What it actually does is said here. */}
            {windowPreset === 'open' && (
              <p className="text-[10px] text-muted-foreground mt-1.5">
                Runs until you stop it. It plans against a rolling horizon and never auto-completes
                the mission.
              </p>
            )}

            {/* Two boxes, two labels. One "Window (UTC)" over both left the operator to guess
                which was the start. */}
            {/* TWO COLUMNS AT MOST, and `lg:grid-cols-3` is gone deliberately. These are VIEWPORT
                queries, not container queries, and this form no longer lives in a 720 px dialog -
                it is in the COP's right column, whose default is 320 px and whose floor is 288 px
                (`copSidePanelWidth.ts`). On any desktop `lg:` is always active, so the third column
                was being applied to a 320 px box: minus the sheet's padding and two gaps, roughly
                90 px per cell, two of them holding `datetime-local` inputs. */}
            {windowPreset !== 'open' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Field id={`${uid}-from`} label="From (UTC)">
                  <Input
                    id={`${uid}-from`}
                    type="datetime-local"
                    value={form.windowStart}
                    onChange={(e) => {
                      setWindowPreset('custom');
                      setForm({ ...form, windowStart: e.target.value });
                    }}
                    className="h-8 text-xs font-mono tabular-nums"
                  />
                  <p className="text-[10px] text-muted-foreground h-3">
                    {localEcho(form.windowStart) ? `Local: ${localEcho(form.windowStart)}` : ''}
                  </p>
                </Field>
                <Field id={`${uid}-to`} label="To (UTC)">
                  <Input
                    id={`${uid}-to`}
                    type="datetime-local"
                    value={form.windowEnd}
                    onChange={(e) => {
                      setWindowPreset('custom');
                      setForm({ ...form, windowEnd: e.target.value });
                    }}
                    className="h-8 text-xs font-mono tabular-nums"
                  />
                  <p className="text-[10px] text-muted-foreground h-3">
                    {localEcho(form.windowEnd) ? `Local: ${localEcho(form.windowEnd)}` : ''}
                  </p>
                </Field>
              </div>
            )}
          </div>
        </div>

        <div
          role="tabpanel"
          id={`${uid}-panel-aircraft`}
          aria-labelledby={`${uid}-tab-aircraft`}
          hidden={step !== 'aircraft'}
        >
          <CoverageAircraftStep
            drones={drones}
            dronesStatus={dronesStatus}
            onRetryDrones={onRetryDrones}
            memberIds={form.memberIds}
            assignedDroneId={assignedDroneId}
            onToggle={toggleMember}
            onMove={moveMember}
            plansByDrone={plansByDrone}
            enduranceSourceByDrone={enduranceSourceByDrone}
            cruiseSourceByDrone={cruiseSourceByDrone}
            inputsStatus={planInputs.status}
            onRetryInputs={planInputs.reload}
            plansComputable={notComputableReason === null}
            notComputableReason={notComputableReason}
          />
        </div>

        <div
          role="tabpanel"
          id={`${uid}-panel-rules`}
          aria-labelledby={`${uid}-tab-rules`}
          hidden={step !== 'rules'}
        >
          <CoverageRulesStep
            uid={uid}
            coverageTargetPct={form.coverageTargetPct}
            batteryNormalPct={form.batteryNormalPct}
            batteryCriticalPct={form.batteryCriticalPct}
            reliefApproach={form.reliefApproach}
            // The LIVE roster, so the swap-approach group's rotation-of-one note appears and
            // disappears as aircraft are added and removed on the previous tab.
            memberCount={form.memberIds.length}
            onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
          />
        </div>
      </fieldset>

      {/* THE PREVIEW. Always visible, whichever step is open, because every field on every step
          changes it - that is the whole point, and a picture behind a tab would be a picture
          nobody watches while they work.

          Suppressed while the figures are still arriving or have failed: a chart drawn from
          half-read inputs is worse than no chart, and step 2 says why in both cases. */}
      <div className="space-y-2 pt-1 border-t border-border">
        {planInputs.status === 'ready' ? (
          <>
            <CoverageTimelinePreview view={projection.view} summary={previewSummary} />
            <CoverageProjectionVerdict verdict={projection.verdict} targetPct={passMarkPct} />
          </>
        ) : planInputs.status === 'loading' ? (
          <p className="text-[11px] text-muted-foreground">Working out the projection…</p>
        ) : (
          /* NEVER JUST ABSENT. The sentence and the retry lived inside the aircraft step, which is
             `hidden` on the two tabs the wizard actually opens on - so a failed figures read left
             a blank where the preview belongs, indistinguishable from a build that has no preview,
             with the way out behind a tab nobody had a reason to open. Hoisted for the same reason
             the fleet's own retry is. */
          <p className="flex flex-wrap items-center gap-2 text-[11px] text-status-amber-fg">
            <span className="flex items-start gap-1.5">
              <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
              Endurance and dock figures could not be read, so this rotation cannot be previewed
            </span>
            <button
              type="button"
              onClick={planInputs.reload}
              className="min-h-6 px-2 py-1 rounded border border-border text-primary hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              Retry the figures
            </button>
          </p>
        )}
      </div>

      {/* THE COMMIT LINE. Always visible, whichever step is open, because this is what the press
          does. Monospace and uppercase like every other telemetry read on this panel. */}
      <p
        data-testid="attach-commit-summary"
        className="text-[11px] font-mono tabular-nums uppercase tracking-wider text-muted-foreground"
      >
        {commitSummary}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="h-8 text-xs"
          disabled={busy || blockers.length > 0}
          aria-busy={submitting}
          onClick={() => void handleAttach()}
        >
          {submitting ? 'Attaching…' : 'Attach coverage'}
        </Button>
        {/* THE WAY OUT OF A FAILED FLEET READ, hoisted out of the closed panel. The refusal beside
            it says the fleet could not be loaded; the control that fixes it has to be reachable
            from the same place, not from a tab the operator has no reason to open. */}
        {dronesStatus === 'error' && (
          <button
            type="button"
            onClick={onRetryDrones}
            className="min-h-7 px-2 py-1 rounded border border-border text-xs text-primary hover:text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            Retry the fleet
          </button>
        )}
        {/* Advisory, not a blocker: readiness is the server's call and an operator may legitimately
            configure a rotation before its aircraft come back. */}
        {unavailablePicked > 0 && blockers.length === 0 && (
          <span className="text-xs text-status-amber-fg">
            {unavailablePicked} of the {form.memberIds.length} aircraft picked
            {unavailablePicked === 1 ? ' is' : ' are'} offline or in maintenance
          </span>
        )}
        {/* The row that says this in full is on a step that is hidden from the other two, and the
            press is available from all three. What it costs is not a missing bar on a chart. */}
        {unsizablePicked > 0 && blockers.length === 0 && (
          <span className="flex items-start gap-1.5 text-xs text-status-amber-fg">
            <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            {unsizablePicked} of the {form.memberIds.length} aircraft picked cannot be sized:
            {unsizablePicked === 1 ? ' it is' : ' they are'} left out of the estimate, and the
            planner relieves {unsizablePicked === 1 ? 'it' : 'them'} as soon as
            {unsizablePicked === 1 ? ' it reaches' : ' they reach'} the station
          </span>
        )}
        {/* Refused BEFORE the press, and named - every one of these is knowable here, and sending
            them to the API produces a toast naming none. The step is named too, because the field
            that fixes it may be behind a tab the operator is not looking at. */}
        {blockers.length > 0 && (
          <span className="flex items-start gap-1.5 text-xs text-status-amber-fg">
            <AlertTriangle className="size-3.5 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
            <span>
              {blockers[0].message}
              {blockers[0].step !== step && (
                <>
                  {' ('}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={() => setStep(blockers[0].step)}
                  >
                    {STEP_LABEL[blockers[0].step]}
                  </button>
                  {')'}
                </>
              )}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
